import type { ComponentProps } from 'react'

import { Button } from '@atoms'
import { DropdownMenu, DropdownMenuContent, DropdownMenuIconItem, DropdownMenuTrigger } from '@atoms/DropdownMenu'
import { PageHeader } from '@components/layouts/PageHeader'
import SearchField from '@components/molecules/SearchField'
import { PageHeaderNav } from '@molecules/PageHeaderNav'
import Icon from '@components/utils/Icon'

import type { ComponentDoc, PropDocs } from '../types'

type PageHeaderProps = ComponentProps<typeof PageHeader>

function FullWidthPageHeader(props: PageHeaderProps) {
  return (
    <div className="tw:w-full">
      <PageHeader {...props} />
    </div>
  )
}

const props: PropDocs<PageHeaderProps> = {
  heading: { type: 'string', required: true, description: 'The page h1. Also sets the browser tab title.' },
  icon: {
    type: 'IconTypes',
    description: 'Shown in a chip before the heading. Use the same icon as the sidebar item.',
  },
  subtitle: { type: 'ReactNode', description: 'One muted line under the heading.' },
  actions: {
    type: 'ReactNode',
    description: 'Buttons on the right. One primary action, plus an overflow menu if needed.',
  },
  search: {
    type: 'ReactNode',
    description: 'A search field on the bottom border. Adds space below the header.',
  },
  nav: {
    type: 'ReactNode',
    description: 'A PageHeaderNav of sibling pages, rendered as a white strip directly under the header.',
  },
  documentTitle: {
    type: 'boolean',
    default: 'true',
    description:
      'Sets the browser tab title through Inertia. Turn off only when mounting the header as an island on a v1 page.',
  },
}

function WithActions() {
  return (
    <FullWidthPageHeader
      icon="community"
      heading="Page header"
      subtitle="686 supporters"
      actions={
        <>
          <Button leadingIcon="user-plus-01">Add new supporter</Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button rank="secondary" ariaLabel="More" className="tw:border tw:border-primary-200 tw:bg-white tw:px-2">
                <Icon type="dots-horizontal" className="tw:h-4.5 tw:w-4.5 tw:rotate-90 tw:stroke-2" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" sideOffset={8} className="tw:w-80 tw:rounded-xl tw:p-2">
              <DropdownMenuIconItem icon="calendar" label="Events" description="Plan and roster this group's events." />
              <DropdownMenuIconItem icon="mail-01" label="Email blasts" description="Write to everyone in the group." />
              <DropdownMenuIconItem icon="send" label="SMS blasts" description="Text everyone in the group." />
            </DropdownMenuContent>
          </DropdownMenu>
        </>
      }
    />
  )
}

function WithSearch() {
  return (
    <FullWidthPageHeader
      icon="community"
      heading="Page header"
      subtitle="Create and manage supporter groups"
      actions={<Button trailingIcon="plus-circle">Add new group</Button>}
      search={
        <div className="tw:max-w-xs tw:[&>div>label]:font-bold">
          <SearchField
            fieldName="search"
            displayName="Search groups"
            inputWrapperClassName="tw:min-h-12 tw:border-gray-cool-200"
            onChange={() => {}}
          />
        </div>
      }
    />
  )
}

function WithNav() {
  return (
    <FullWidthPageHeader
      icon="mail-01"
      heading="Inbox"
      nav={
        <PageHeaderNav
          ariaLabel="Inbox"
          items={[
            { label: 'SMS', href: '#sms', current: true, count: 4 },
            { label: 'Email', href: '#email' },
          ]}
        />
      }
    />
  )
}

const doc: ComponentDoc<PageHeaderProps> = {
  slug: 'page-header',
  name: 'PageLayout.Header',
  tier: 'layout',
  description:
    'The top of every Inertia page: icon, heading, subtitle and actions. Use it as PageLayout.Header inside PageLayout.Root.',
  importStatement: "import { PageLayout } from '@components/layouts/PageLayout'",
  sourcePath: 'app/frontend/components/layouts/PageHeader.tsx',
  component: FullWidthPageHeader,
  props,
  examples: [
    {
      title: 'Heading only',
      variants: [{ heading: 'Page header' }],
    },
    {
      title: 'Icon and subtitle',
      variants: [{ icon: 'community', heading: 'Page header', subtitle: 'Create and manage supporter groups' }],
    },
    {
      title: 'With actions',
      note: 'One primary action. Put the rest in a DropdownMenu of DropdownMenuIconItems. This is the group page header.',
      render: WithActions,
      code: `<PageLayout.Header
  icon="community"
  heading="Page header"
  subtitle="686 supporters"
  actions={
    <>
      <Button leadingIcon="user-plus-01">Add new supporter</Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button rank="secondary" ariaLabel="More" className="tw:border tw:border-primary-200 tw:bg-white tw:px-2">
            <Icon type="dots-horizontal" className="tw:h-4.5 tw:w-4.5 tw:rotate-90 tw:stroke-2" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" sideOffset={8} className="tw:w-80 tw:rounded-xl tw:p-2">
          <DropdownMenuIconItem icon="calendar" label="Events" description="Plan and roster this group's events." />
          <DropdownMenuIconItem icon="mail-01" label="Email blasts" description="Write to everyone in the group." />
          <DropdownMenuIconItem icon="send" label="SMS blasts" description="Text everyone in the group." />
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  }
/>`,
    },
    {
      title: 'With search',
      note: 'The search field sits on the bottom border. Set hideLabel={false} on SearchField so the label shows. This is the Supporter Groups header.',
      render: WithSearch,
      code: `<PageLayout.Header
  icon="community"
  heading="Page header"
  subtitle="Create and manage supporter groups"
  actions={<Button trailingIcon="plus-circle">Add new group</Button>}
  search={
    <div className="tw:max-w-xs tw:[&>div>label]:font-bold">
      <SearchField
        fieldName="search"
        displayName="Search groups"
        inputWrapperClassName="tw:min-h-12 tw:border-gray-cool-200"
        onChange={(event) => searchFn(event, partialReloadKeys, setLoading)}
      />
    </div>
  }
/>`,
    },
    {
      title: 'With page nav',
      note: 'A PageHeaderNav of sibling pages sits in a white strip under the header.',
      render: WithNav,
      code: `<PageLayout.Header
  icon="mail-01"
  heading="Inbox"
  nav={
    <PageHeaderNav
      ariaLabel="Inbox"
      items={[
        { label: 'SMS', href: smsInboxRoute, current: true, count: 4 },
        { label: 'Email', href: emailInboxRoute },
      ]}
    />
  }
/>`,
    },
  ],
  usage: {
    do: [
      'Render one on every Inertia page, directly inside PageLayout.Root.',
      'Always give it an icon. Use the same icon as the sidebar item.',
      'Put one primary action in actions. Put the rest in a DropdownMenu of DropdownMenuIconItems.',
      'Use search to filter the page.',
      'Use nav, with a PageHeaderNav, when one sidebar item covers several sibling pages.',
    ],
    dont: [
      'Breadcrumbs. The layout renders them.',
      'A second heading in PageLayout.Body. The header is the h1.',
      'Form fields in search. It only filters the page.',
      'search and nav together. The search field overlaps the nav strip.',
      'v1 ERB pages, for now. Most get it when they move to Inertia. The exceptions are the group page header island and the PageHeader island (@components/layouts/PageHeaderIsland, set @page_header in the controller; its optional action is a {label, href, icon} link), both mounted with documentTitle={false}.',
    ],
  },
  a11y: [
    'The heading is the page h1 and sets the tab title.',
    'The icon is decorative. The heading carries the meaning.',
  ],
}

export default doc
